import { Theme } from "@mui/material/styles";

import { ChartDataPoint } from "./types";

export const Amber = "#FFB424";
export const Blue = "#3B82F6";
export const Coral = "#FB7185";
export const Indigo = "#5555E7";
export const Lime = "#A3E635";
export const Megenta = "#D946EF";
export const Orange = "#F97316";
export const Pink = "#EC4899";
export const Purple = "#8B5CF6";
export const Red = "#FF3947";
export const Teal = "#09CFAF";
export const Turquoise = "#06B6D4";

export const Success = "#08AC92";
export const Danger = "#ED4A55";
export const Neutral = "#BFBFFF";

/**
 * Array of all chart colors used for general data series.
 * The order is used for auto-populating chart colors.
 */
export const AllColors = [Indigo, Teal, Amber, Red, Purple, Blue, Turquoise, Pink, Orange, Lime, Coral, Megenta];

/**
 * Array of colors specifically for Yes/No (binary) charts.
 * Used to distinguish between two categories.
 */
export const YesNoColors = [Teal, Red];

type ChartDataPointWithOptionalColor = Omit<ChartDataPoint, "color"> & {
  color?: string;
};

/**
 * Automatically populates missing colors for chart data points using the default color palette.
 *
 * @param dataPoints - Array of chart data points, some of which may have optional color properties
 * @returns Array of chart data points with all color properties populated
 *
 * @example
 * ```typescript
 * const dataPoints = [
 *   { label: "Category A", value: 100 },
 *   { label: "Category B", value: 200, color: "#ff0000" },
 *   { label: "Category C", value: 150 }
 * ];
 *
 * const coloredDataPoints = autoPopulateChartColors(dataPoints);
 * // Result: All data points now have color properties assigned
 * ```
 */
export const autoPopulateChartColors = (dataPoints: ChartDataPointWithOptionalColor[]): ChartDataPoint[] => {
  let currentColorIndex = 0;

  return dataPoints.map((dataPoint) => {
    if (dataPoint.color) {
      return dataPoint as ChartDataPoint;
    }

    return { ...dataPoint, color: AllColors[currentColorIndex++ % AllColors.length] };
  });
};

/**
 * Automatically populates missing colors for Yes/No chart data points using the specialized Yes/No color palette.
 *
 * @param dataPoints - Array of chart data points, some of which may have optional color properties
 * @returns Array of chart data points with all color properties populated using Yes/No color scheme
 *
 * @example
 * ```typescript
 * const yesNoData = [
 *   { label: "Yes", value: 75 },
 *   { label: "No", value: 25 }
 * ];
 *
 * const coloredYesNoData = autoPopulateYesNoChartColors(yesNoData);
 * // Result: Data points now have appropriate Yes/No colors assigned
 * ```
 */
export const autoPopulateYesNoChartColors = (dataPoints: ChartDataPointWithOptionalColor[]): ChartDataPoint[] => {
  let currentColorIndex = 0;

  return dataPoints.map((dataPoint) => {
    if (dataPoint.color) {
      return dataPoint as ChartDataPoint;
    }

    return { ...dataPoint, color: YesNoColors[currentColorIndex++ % YesNoColors.length] };
  });
};

export const getFadedColor = (theme: Theme) => theme.palette.surface.neutral.quiet;
export const getBackgroundColor = (theme: Theme) => theme.palette.surface.neutral.supershy;
